import React, { useEffect, useMemo, useState } from "react";

import { resolveRuntimeConfig } from "@/config/runtimeConfig";
import { SafeContext, type SafeConnection } from "@/safe/SafeContextValue";
import {
  resolveInSafeConnection,
  resolveInitialSafeConnection,
  resolveOutsideSafeConnection
} from "@/safe/safeConnectionState";
import {
  createInSafeProvider,
  createOutsideSafeProvider,
  createSafeAppsSdk,
  detectSafe
} from "@/safe/safeRuntimeAdapter";
import {
  evaluateSafeParentOrigin,
  getDocumentReferrerOrigin,
  isEmbeddedRuntime,
  resolveRuntimeAuthenticity,
  resolveRuntimeConfigPosture,
  resolveRuntimeDowngradePolicy,
  type RuntimeSafeParentOrigin
} from "@/safe/runtimePolicy";

export function SafeProvider({ children }: { children: React.ReactNode }) {
  const appRuntimeConfig = useMemo(() => resolveRuntimeConfig(), []);
  const authenticity = useMemo(
    () => resolveRuntimeAuthenticity(appRuntimeConfig),
    [appRuntimeConfig]
  );
  const runtimeConfig = useMemo(
    () => resolveRuntimeConfigPosture(appRuntimeConfig),
    [appRuntimeConfig]
  );
  const parentOriginAllowlist = useMemo(
    () => appRuntimeConfig.safe.parentOriginAllowlist,
    [appRuntimeConfig]
  );
  const downgradePolicy = useMemo(
    () => resolveRuntimeDowngradePolicy(appRuntimeConfig.safe),
    [appRuntimeConfig]
  );
  const initialParentOrigin = useMemo(
    () =>
      evaluateSafeParentOrigin({
        embedded: isEmbeddedRuntime(),
        parentOrigin: getDocumentReferrerOrigin(),
        allowlist: parentOriginAllowlist
      }),
    [parentOriginAllowlist]
  );

  const [state, setState] = useState<SafeConnection>(() =>
    resolveInitialSafeConnection({
      authenticity,
      config: runtimeConfig,
      parentOrigin: initialParentOrigin,
      downgradePolicy
    })
  );

  useEffect(() => {
    let cancelled = false;

    function setOutsideSafeState(params: {
      parentOrigin: RuntimeSafeParentOrigin;
      blockedByParentOrigin?: boolean;
    }) {
      const { parentOrigin, blockedByParentOrigin = false } = params;
      const rpcUrl = appRuntimeConfig.endpoints.mainnetRpcUrl;

      if (rpcUrl && !blockedByParentOrigin) {
        setState(
          resolveOutsideSafeConnection({
            authenticity,
            config: runtimeConfig,
            parentOrigin,
            downgradePolicy,
            providerStatus: "ready",
            provider: createOutsideSafeProvider(rpcUrl)
          })
        );
        return;
      }

      setState(
        resolveOutsideSafeConnection({
          authenticity,
          config: runtimeConfig,
          parentOrigin,
          downgradePolicy,
          providerStatus: blockedByParentOrigin ? "unavailable" : "missing-rpc"
        })
      );
    }

    async function boot() {
      const embedded = isEmbeddedRuntime();
      const referrerParentOrigin = evaluateSafeParentOrigin({
        embedded,
        parentOrigin: getDocumentReferrerOrigin(),
        allowlist: parentOriginAllowlist
      });

      if (embedded && referrerParentOrigin.status !== "ALLOWLISTED") {
        setOutsideSafeState({
          parentOrigin: referrerParentOrigin,
          blockedByParentOrigin: true
        });
        return;
      }

      const sdk = createSafeAppsSdk(parentOriginAllowlist);
      const detection = await detectSafe(sdk);
      if (cancelled) return;

      const resolvedParentOrigin = detection.parentOrigin
        ? evaluateSafeParentOrigin({
            embedded,
            parentOrigin: detection.parentOrigin,
            allowlist: parentOriginAllowlist
          })
        : referrerParentOrigin;

      if (embedded && resolvedParentOrigin.status !== "ALLOWLISTED") {
        setOutsideSafeState({
          parentOrigin: resolvedParentOrigin,
          blockedByParentOrigin: true
        });
        return;
      }

      if (detection.safe) {
        setState(
          resolveInSafeConnection({
            authenticity,
            config: runtimeConfig,
            parentOrigin: resolvedParentOrigin,
            downgradePolicy,
            safe: detection.safe,
            sdk,
            provider: createInSafeProvider(detection.safe, sdk)
          })
        );
        return;
      }

      setOutsideSafeState({ parentOrigin: resolvedParentOrigin });
    }

    void boot();
    return () => {
      cancelled = true;
    };
  }, [appRuntimeConfig, authenticity, downgradePolicy, parentOriginAllowlist, runtimeConfig]);

  const value = useMemo(() => state, [state]);
  return <SafeContext.Provider value={value}>{children}</SafeContext.Provider>;
}
